import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../../components/ui/Sidebar";
import Sidebar from "../../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";
import { useGetVendors } from "pages/vendor-management/useGetVendors";
import { useCreatePurchaseCreditNote } from "./useCreatePurchaseCreditNote";
import { useGetVendorBills } from "./useGetVendorBills";

const TAX_TYPES = [
  "GCT",
  "GST",
  "HST",
  "IGST",
  "PST",
  "QST",
  "Sales Tax",
  "VAT",
];

const EMPTY_LINE = () => ({
  id: Date.now() + Math.random(),
  description: "",
  qty: 1,
  price: "",
  taxType: "VAT",
  taxRate: 15,
});

const EMPTY_FORM = {
  vendorId: "",
  vendorName: "",
  billId: "",
  billNumber: "",
  creditNoteNumber: "",
  creditNoteDate: new Date().toISOString().split("T")[0],
  lines: [],
  notes: "",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const calcLine = (line) => {
  const qty = parseFloat(line.qty) || 0;
  const price = parseFloat(line.price) || 0;
  const taxRate = parseFloat(line.taxRate) || 0;
  const subtotal = qty * price;
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;
  return { subtotal, taxAmount, total, taxRate };
};

// ── Shared UI ─────────────────────────────────────────────────────────────────
const SectionCard = ({
  icon,
  iconBg,
  iconColor,
  title,
  subtitle,
  defaultExpanded = true,
  action,
  children,
}) => {
  const [exp, setExp] = useState(defaultExpanded);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}
            >
              <Icon name={icon} size={20} className={iconColor} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground dark:text-white">
                {title}
              </h3>
              <p className="text-sm text-muted-foreground dark:text-gray-300">
                {subtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {action}
            <Button
              variant="ghost"
              size="sm"
              iconName={exp ? "ChevronUp" : "ChevronDown"}
              onClick={() => setExp(!exp)}
            />
          </div>
        </div>
        <AnimatePresence>
          {exp && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const TI = ({ error, className = "", ...props }) => (
  <input
    {...props}
    className={`w-full px-3 py-2.5 border rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${error ? "border-red-400" : "border-gray-200 dark:border-gray-600"} ${className}`}
  />
);

const SI = ({ children, ...props }) => (
  <select
    {...props}
    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
  >
    {children}
  </select>
);

const Field = ({ label, required, error, hint, children }) => (
  <div>
    {label && (
      <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    )}
    {children}
    {hint && !error && (
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    )}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

// ── Wizard ────────────────────────────────────────────────────────────────────
const PurchaseCreditNoteWizardContent = () => {
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("pages");
  const existing = location.state?.creditNote || null;
  const prefill = location.state?.prefillExpense || null;

  const [form, setForm] = useState({ ...EMPTY_FORM, lines: [EMPTY_LINE()] });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { data: vendors = [] } = useGetVendors();
  const createCreditNote = useCreatePurchaseCreditNote();
  const { data: vendorBills = [] } = useGetVendorBills(form.vendorId);

  useEffect(() => {
    if (existing) {
      setForm({
        ...EMPTY_FORM,
        ...existing,
        lines: existing.lines?.length ? existing.lines : [EMPTY_LINE()],
      });
    } else if (prefill) {
      setForm((p) => ({
        ...p,
        vendorName: prefill.vendorName || "",
        billNumber: prefill.expenseNumber || "",
      }));
    }
  }, [existing, prefill]);

  const set = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const addLine = () =>
    setForm((p) => ({ ...p, lines: [...p.lines, EMPTY_LINE()] }));
  const removeLine = (id) =>
    setForm((p) => ({ ...p, lines: p.lines.filter((l) => l.id !== id) }));
  const updateLine = (id, field, value) =>
    setForm((p) => ({
      ...p,
      lines: p.lines.map((l) => (l.id !== id ? l : { ...l, [field]: value })),
    }));

  // Totals
  const lineCalcs = form.lines.map(calcLine);
  const totalAmount = lineCalcs.reduce((s, c) => s + c.subtotal, 0);
  const totalVat = lineCalcs.reduce((s, c) => s + c.taxAmount, 0);
  const netAmount = totalAmount + totalVat;

  const validate = () => {
    const e = {};
    if (!form.vendorId) e.vendorId = "Vendor is required.";
    if (!form.creditNoteNumber.trim())
      e.creditNoteNumber = "Credit note number is required.";
    if (!form.creditNoteDate)
      e.creditNoteDate = "Credit note date is required.";
    if (!form.lines.length) e.lines = "At least one line item is required.";
    form.lines.forEach((l, i) => {
      if (!l.description.trim()) e[`desc_${i}`] = "Description required.";
      if (!l.price || parseFloat(l.price) <= 0)
        e[`price_${i}`] = "Price required.";
    });
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      toast.error(t("purchaseRefundWizard.saveError"));
      return;
    }

    setSaving(true);
    try {
      const lineItems = form.lines.map((l) => ({
        description: l.description.trim(),
        quantity: parseFloat(l.qty) || 1,
        unitPrice: parseFloat(l.price) || 0,
        discountAmount: 0,
        taxRate: parseFloat(l.taxRate) || 0,
      }));

      await createCreditNote.mutateAsync({
        documentType: "operating_expense",
        vendorId: form.vendorId,
        vendorName: form.vendorName.trim(),
        billId: form.billId || null,
        originalBillNumber: form.billNumber || "",
        documentDate: form.creditNoteDate,
        reason: form.notes || "",
        lineItems,
      });

      toast.success(t("purchaseRefundWizard.creditNoteRecorded"));
      navigate("/purchases/refunds");
    } catch (err) {
      toast.error(err?.message || t("purchaseRefundWizard.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) =>
    (parseFloat(n) || 0).toLocaleString("en-SA", { minimumFractionDigits: 2 });

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Sticky header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-40 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-8"
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/purchases/refunds")}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                >
                  <Icon name="ArrowLeft" size={20} />
                </button>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <div className="w-6 h-6 bg-violet-100 dark:bg-violet-900/20 rounded flex items-center justify-center">
                      <Icon
                        name="FileMinus"
                        size={13}
                        className="text-violet-600 dark:text-violet-400"
                      />
                    </div>
                    <span className="text-xs font-medium text-violet-600 dark:text-violet-400 uppercase tracking-wide">
                      {t("purchaseRefundWizard.purchaseCreditNote")}
                    </span>
                  </div>
                  <h1 className="text-2xl font-bold text-foreground dark:text-white">
                    {existing
                      ? t("purchaseRefundWizard.editCreditNote")
                      : t("purchaseRefundWizard.newCreditNote")}
                  </h1>
                  <p className="text-sm text-muted-foreground dark:text-gray-300">
                    {t("purchaseRefundWizard.creditNoteDescription")}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => navigate("/purchases/refunds")}
                >
                  {t("purchaseRefundWizard.cancel")}
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  iconName={saving ? "Loader2" : "Save"}
                  onClick={handleSave}
                  disabled={saving}
                  className="shadow-sm"
                >
                  {saving
                    ? t("purchaseRefundWizard.saving")
                    : existing
                      ? t("purchaseRefundWizard.updateCreditNote")
                      : t("purchaseRefundWizard.saveCreditNote")}
                </Button>
              </div>
            </div>

            {/* Step pills */}
            <div className="flex items-center gap-2">
              {[
                { label: "Vendor & Bill", icon: "Truck" },
                { label: "Credit Note #", icon: "FileMinus" },
                { label: "Line Items", icon: "List" },
                { label: "Summary", icon: "BarChart2" },
              ].map((step, i) => (
                <React.Fragment key={step.label}>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-7 h-7 rounded-full bg-violet-100 dark:bg-violet-900/20 flex items-center justify-center">
                      <Icon
                        name={step.icon}
                        size={13}
                        className="text-violet-600 dark:text-violet-400"
                      />
                    </div>
                    <span className="hidden md:block font-medium text-foreground dark:text-white">
                      {step.label}
                    </span>
                  </div>
                  {i < 3 && (
                    <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600 hidden md:block" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3 space-y-6">
            {/* Section 1: Vendor & Bill */}
            <SectionCard
              icon="Truck"
              iconBg="bg-violet-100 dark:bg-violet-900/20"
              iconColor="text-violet-600 dark:text-violet-400"
              title="Vendor & Bill Details"
              subtitle="Which vendor issued this credit note and against which bill?"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Vendor" required error={errors.vendorId}>
                  <select
                    value={form.vendorId}
                    onChange={(e) => {
                      const v = vendors.find(
                        (x) => String(x.id || x._id) === e.target.value,
                      );
                      setForm((p) => ({
                        ...p,
                        vendorId: e.target.value,
                        vendorName: v?.name || "",
                        billId: "",
                        billNumber: "",
                      }));
                      if (errors.vendorId)
                        setErrors((p) => ({ ...p, vendorId: "" }));
                    }}
                    className={`w-full px-3 py-2.5 border rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${errors.vendorId ? "border-red-400" : "border-gray-200 dark:border-gray-600"}`}
                  >
                    <option value="">Select vendor</option>
                    {vendors.map((v) => (
                      <option key={v.id || v._id} value={v.id || v._id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Bill #"
                  hint="Link the bill this credit note references (inherits its account type)"
                >
                  <select
                    value={form.billId}
                    disabled={!form.vendorId}
                    onChange={(e) => {
                      const b = vendorBills.find(
                        (x) => String(x.id || x._id) === e.target.value,
                      );
                      setForm((p) => ({
                        ...p,
                        billId: e.target.value,
                        billNumber: b?.documentNumber || "",
                      }));
                    }}
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent text-sm disabled:opacity-50 font-mono"
                  >
                    <option value="">
                      {form.vendorId
                        ? "Select bill (optional)"
                        : "Select a vendor first"}
                    </option>
                    {vendorBills.map((b) => (
                      <option key={b.id || b._id} value={b.id || b._id}>
                        {b.documentNumber} — SR{" "}
                        {Number(b.totalAmount || 0).toLocaleString("en-SA", {
                          minimumFractionDigits: 2,
                        })}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Credit Note #"
                  required
                  error={errors.creditNoteNumber}
                  hint="e.g. PCN-0001"
                >
                  <TI
                    value={form.creditNoteNumber}
                    placeholder="PCN-0001"
                    error={errors.creditNoteNumber}
                    className="font-mono"
                    onChange={(e) => set("creditNoteNumber", e.target.value)}
                  />
                </Field>
                <Field
                  label="Credit Note Date"
                  required
                  error={errors.creditNoteDate}
                >
                  <TI
                    type="date"
                    value={form.creditNoteDate}
                    error={errors.creditNoteDate}
                    onChange={(e) => set("creditNoteDate", e.target.value)}
                  />
                </Field>
              </div>
            </SectionCard>

            {/* Section 2: Line Items */}
            <SectionCard
              icon="List"
              iconBg="bg-primary-100 dark:bg-primary-900/20"
              iconColor="text-primary"
              title="Line Items"
              subtitle="Products or services included in this credit note"
              action={
                <Button
                  variant="primary"
                  size="sm"
                  iconName="Plus"
                  onClick={addLine}
                >
                  Add Item
                </Button>
              }
            >
              <div className="space-y-4">
                {errors.lines && (
                  <p className="text-xs text-red-500">{errors.lines}</p>
                )}

                {/* Column headers */}
                <div className="grid grid-cols-[2fr_70px_90px_100px_70px_90px_36px] gap-2 px-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  <span>Description</span>
                  <span>Qty</span>
                  <span>Price (SR)</span>
                  <span>Tax Type</span>
                  <span>Rate %</span>
                  <span>Amount (SR)</span>
                  <span></span>
                </div>

                <AnimatePresence>
                  {form.lines.map((line, index) => {
                    const calc = calcLine(line);
                    return (
                      <motion.div
                        key={line.id}
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -16 }}
                        transition={{ duration: 0.2 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                              <span className="text-xs font-semibold text-primary">
                                {index + 1}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-foreground dark:text-white">
                              Line Item {index + 1}
                            </span>
                          </div>
                          {form.lines.length > 1 && (
                            <button
                              onClick={() => removeLine(line.id)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            >
                              <Icon name="Trash2" size={13} />
                            </button>
                          )}
                        </div>

                        {/* Description */}
                        <div className="mb-3">
                          <TI
                            value={line.description}
                            placeholder="Product or service description"
                            error={errors[`desc_${index}`]}
                            onChange={(e) =>
                              updateLine(line.id, "description", e.target.value)
                            }
                          />
                          {errors[`desc_${index}`] && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors[`desc_${index}`]}
                            </p>
                          )}
                        </div>

                        {/* Grid */}
                        <div className="grid grid-cols-[2fr_70px_90px_100px_70px_90px_36px] gap-2 items-start">
                          <div />
                          <TI
                            type="number"
                            value={line.qty}
                            min="0"
                            step="1"
                            onChange={(e) =>
                              updateLine(line.id, "qty", e.target.value)
                            }
                          />
                          <div>
                            <TI
                              type="number"
                              value={line.price}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              error={errors[`price_${index}`]}
                              onChange={(e) =>
                                updateLine(line.id, "price", e.target.value)
                              }
                            />
                            {errors[`price_${index}`] && (
                              <p className="text-xs text-red-500 mt-1">
                                {errors[`price_${index}`]}
                              </p>
                            )}
                          </div>
                          <SI
                            value={line.taxType}
                            onChange={(e) =>
                              updateLine(line.id, "taxType", e.target.value)
                            }
                          >
                            {TAX_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </SI>
                          <TI
                            type="number"
                            value={line.taxRate}
                            min="0"
                            max="100"
                            step="0.01"
                            placeholder="15"
                            onChange={(e) =>
                              updateLine(line.id, "taxRate", e.target.value)
                            }
                          />
                          <div className="px-3 py-2.5 bg-violet-50 dark:bg-violet-900/20 rounded-lg text-violet-700 dark:text-violet-400 font-semibold text-sm text-end">
                            {fmt(calc.total)}
                          </div>
                          <div />
                        </div>

                        {parseFloat(line.price) > 0 && (
                          <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground border-t border-gray-200 dark:border-gray-600 pt-2">
                            <span>
                              Subtotal:{" "}
                              <span className="text-foreground font-medium">
                                SR {fmt(calc.subtotal)}
                              </span>
                            </span>
                            <span>
                              {line.taxType} ({calc.taxRate}%):{" "}
                              <span className="text-blue-500 font-medium">
                                SR {fmt(calc.taxAmount)}
                              </span>
                            </span>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                <div className="flex justify-center pt-2">
                  <Button
                    variant="outline"
                    size="md"
                    iconName="Plus"
                    onClick={addLine}
                    className="border-dashed border-2 hover:border-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 w-full"
                  >
                    Add Another Item
                  </Button>
                </div>
              </div>
            </SectionCard>

            {/* Notes */}
            <SectionCard
              icon="FileText"
              iconBg="bg-gray-100 dark:bg-gray-700"
              iconColor="text-muted-foreground"
              title="Notes"
              subtitle="Additional information about this credit note"
              defaultExpanded={false}
            >
              <textarea
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Additional notes..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
              />
            </SectionCard>

            {/* Bottom action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 flex justify-end gap-3"
            >
              <Button
                variant="outline"
                size="md"
                onClick={() => navigate("/purchases/refunds")}
              >
                {t("purchaseRefundWizard.cancel")}
              </Button>
              <Button
                variant="primary"
                size="md"
                iconName={saving ? "Loader2" : "Save"}
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? t("purchaseRefundWizard.saving")
                  : existing
                    ? t("purchaseRefundWizard.updateCreditNote")
                    : t("purchaseRefundWizard.saveCreditNote")}
              </Button>
            </motion.div>
          </div>

          {/* Right sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 space-y-4">
              {/* Summary */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-2 mb-5">
                  <Icon name="BarChart2" size={18} className="text-primary" />
                  <h3 className="text-base font-semibold text-foreground dark:text-white">
                    Summary
                  </h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total</span>
                    <span className="font-medium text-foreground">
                      SR {fmt(totalAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">VAT / Tax</span>
                    <span className="font-medium text-foreground">
                      SR {fmt(totalVat)}
                    </span>
                  </div>
                  <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-foreground">
                        Net Amount
                      </span>
                      <span className="text-lg font-bold text-violet-600 dark:text-violet-400">
                        SR {fmt(netAmount)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-2 text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Vendor</span>
                    <span className="font-medium text-foreground truncate max-w-[110px]">
                      {form.vendorName || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>CN #</span>
                    <span className="font-medium text-foreground font-mono">
                      {form.creditNoteNumber || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bill #</span>
                    <span className="font-medium text-foreground font-mono">
                      {form.billNumber || "—"}
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Checklist */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Icon
                    name="CheckCircle2"
                    size={18}
                    className="text-primary"
                  />
                  <h3 className="text-base font-semibold text-foreground dark:text-white">
                    Checklist
                  </h3>
                </div>
                <div className="space-y-2.5">
                  {[
                    { label: "Vendor", ok: !!form.vendorName.trim() },
                    {
                      label: "Credit Note #",
                      ok: !!form.creditNoteNumber.trim(),
                    },
                    { label: "Date", ok: !!form.creditNoteDate },
                    {
                      label: "Line items",
                      ok:
                        form.lines.length > 0 &&
                        form.lines.every(
                          (l) =>
                            l.description.trim() && parseFloat(l.price) > 0,
                        ),
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Icon
                          name={item.ok ? "CheckCircle2" : "Circle"}
                          size={15}
                          className={
                            item.ok
                              ? "text-green-500"
                              : "text-gray-300 dark:text-gray-600"
                          }
                        />
                        <span className="text-sm text-muted-foreground">
                          {item.label}
                        </span>
                      </div>
                      <span
                        className={`text-xs font-medium ${item.ok ? "text-green-600 dark:text-green-400" : "text-gray-400"}`}
                      >
                        {item.ok ? "Done" : "Pending"}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PurchaseCreditNoteWizard = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <PurchaseCreditNoteWizardContent />
    </div>
  </SidebarProvider>
);

export default PurchaseCreditNoteWizard;
